if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;
      if (!this.elements.thumbnails) return;

      this.thumbnailArrowTarget = null;
      this.thumbnailArrowSettleTimer = null;
      this.elements.thumbnails.addEventListener('click', this.onThumbnailArrowClick.bind(this), true);
      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    onThumbnailArrowClick(event) {
      const button = event.target && event.target.closest
        ? event.target.closest('.slider-button') : null;
      const thumbnails = this.elements.thumbnails;
      if (!button || !thumbnails || !thumbnails.contains(button) || button.disabled
        || thumbnails.vertical || !thumbnails.slider) return;

      const slider = thumbnails.slider;
      const multiplier = Number(thumbnails.scrollMultiplier) || 1;
      const max = Math.max(0, slider.scrollWidth - slider.clientWidth);
      const step = Math.max(1, Number(button.dataset.step) || 1);
      const offset = Number(thumbnails.sliderItemOffset) || slider.clientWidth;
      const current = Number.isFinite(this.thumbnailArrowTarget)
        ? this.thumbnailArrowTarget
        : Math.max(0, Math.min(max, slider.scrollLeft * multiplier));
      const direction = button.name === 'next' ? 1 : -1;
      const target = Math.max(0, Math.min(max, current + direction * step * offset));

      // SliderComponent retargets from the transient smooth-scroll position and
      // permits targets beyond either edge. Own horizontal thumbnail arrows in
      // capture phase so repeated clicks advance one stable, clamped intent.
      event.preventDefault();
      event.stopImmediatePropagation();
      this.thumbnailArrowTarget = target;

      if (!thumbnails.enableSliderLooping) {
        if (thumbnails.prevButton) {
          if (target <= 0) thumbnails.prevButton.setAttribute('disabled', 'disabled');
          else thumbnails.prevButton.removeAttribute('disabled');
        }
        if (thumbnails.nextButton) {
          if (target >= max) thumbnails.nextButton.setAttribute('disabled', 'disabled');
          else thumbnails.nextButton.removeAttribute('disabled');
        }
      }

      if (target !== current) slider.scrollTo({ left: target * multiplier, behavior: 'smooth' });
      if (this.thumbnailArrowSettleTimer) window.clearTimeout(this.thumbnailArrowSettleTimer);
      this.thumbnailArrowSettleTimer = window.setTimeout(() => {
        this.thumbnailArrowTarget = null;
        this.thumbnailArrowSettleTimer = null;
      }, 800);
    }

    onSlideChanged(event) {
      // A not-yet-measured or filtered slider can dispatch an out-of-range page.
      const current = event && event.detail ? event.detail.currentElement : null;
      const mediaId = current && current.dataset ? current.dataset.mediaId : null;
      if (!mediaId || !this.elements.thumbnails) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      if (!thumbnail) return;
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.elements.thumbnails.vertical) {
        if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;
        this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
        return;
      }

      // A selection flush at the visible left edge hides that earlier images
      // exist. Mirror the clipped-right behaviour (which parks the selection
      // at the left edge to reveal the following strip): park this selection
      // at the right edge so the preceding strip scrolls into view.
      const slider = this.elements.thumbnails.slider;
      let previous = thumbnail.previousElementSibling;
      while (previous && previous.classList.contains('hidden')) previous = previous.previousElementSibling;
      // List padding offsets offsetLeft from scrollLeft, so detect "flush at
      // the visible left edge" from rendered geometry with a small tolerance.
      const flushLeft = previous
        && thumbnail.getBoundingClientRect().left - slider.getBoundingClientRect().left < thumbnail.offsetWidth * 0.25;
      if (flushLeft) {
        slider.scrollTo({
          left: Math.max(0, thumbnail.offsetLeft + thumbnail.offsetWidth - slider.clientWidth),
          behavior: 'smooth'
        });
        return;
      }

      if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;
      slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}

